import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';

export function PrivacyPage() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-teal-50 to-white p-6">
      <div className="max-w-3xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => window.close()}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Close
        </Button>

        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">Privacy Policy</CardTitle>
            <p className="text-sm text-muted-foreground">Last updated: December 2025</p>
          </CardHeader>
          <CardContent className="prose prose-sm max-w-none space-y-6">
            <section>
              <h3 className="text-lg font-semibold">1. Introduction</h3>
              <p className="text-muted-foreground">
                PayMig ("we", "us", or "our") is committed to protecting your privacy.
                This Privacy Policy explains how we collect, use, disclose, and safeguard
                your information when you use our service.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold">2. Information We Collect</h3>
              <p className="text-muted-foreground">We collect the following types of information:</p>
              <ul className="list-disc list-inside text-muted-foreground ml-4 space-y-1">
                <li><strong>Account Information:</strong> Name, email, phone number</li>
                <li><strong>Payment Data:</strong> Transaction history, payment amounts, Swish aliases</li>
                <li><strong>Device Information:</strong> Device type, IP address, browser type</li>
                <li><strong>Location Data:</strong> GPS coordinates (only for WatchPay if enabled)</li>
                <li><strong>Usage Data:</strong> App interactions, feature usage, preferences</li>
              </ul>
            </section>

            <section>
              <h3 className="text-lg font-semibold">3. How We Use Your Information</h3>
              <p className="text-muted-foreground">We use your information to:</p>
              <ul className="list-disc list-inside text-muted-foreground ml-4 space-y-1">
                <li>Provide and maintain our service</li>
                <li>Process payment requests and transactions</li>
                <li>Send notifications about payment activity</li>
                <li>Improve and personalize your experience</li>
                <li>Communicate with you about updates and support</li>
                <li>Ensure security and prevent fraud</li>
              </ul>
            </section>

            <section>
              <h3 className="text-lg font-semibold">4. Data Sharing</h3>
              <p className="text-muted-foreground">
                We do not sell your personal data. We may share information with:
              </p>
              <ul className="list-disc list-inside text-muted-foreground ml-4 space-y-1">
                <li><strong>Swish:</strong> To process payment transactions</li>
                <li><strong>Service Providers:</strong> Cloud hosting, analytics (with data protection agreements)</li>
                <li><strong>Legal Requirements:</strong> When required by law or to protect rights</li>
              </ul>
            </section>

            <section>
              <h3 className="text-lg font-semibold">5. Your GDPR Rights</h3>
              <p className="text-muted-foreground">
                Under the General Data Protection Regulation (GDPR), you have the following rights:
              </p>
              <ul className="list-disc list-inside text-muted-foreground ml-4 space-y-1">
                <li><strong>Right to Access:</strong> Request a copy of your personal data</li>
                <li><strong>Right to Rectification:</strong> Correct inaccurate personal data</li>
                <li><strong>Right to Erasure:</strong> Request deletion of your data ("right to be forgotten")</li>
                <li><strong>Right to Data Portability:</strong> Export your data in a standard format</li>
                <li><strong>Right to Restrict Processing:</strong> Limit how we use your data</li>
                <li><strong>Right to Object:</strong> Object to processing for marketing purposes</li>
                <li><strong>Right to Withdraw Consent:</strong> Withdraw consent at any time</li>
              </ul>
              <p className="text-muted-foreground mt-2">
                You can exercise these rights through your account settings or by contacting us.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold">6. Data Retention</h3>
              <p className="text-muted-foreground">
                We retain your data for as long as your account is active or as needed to
                provide services. Transaction records are kept for 7 years for legal compliance.
                You can request account deletion, which will be processed within 30 days.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold">7. Data Security</h3>
              <p className="text-muted-foreground">
                We implement industry-standard security measures including:
              </p>
              <ul className="list-disc list-inside text-muted-foreground ml-4 space-y-1">
                <li>Encryption of data in transit and at rest</li>
                <li>Secure authentication and session management</li>
                <li>Regular security audits and monitoring</li>
                <li>Access controls and employee training</li>
              </ul>
            </section>

            <section>
              <h3 className="text-lg font-semibold">8. Children's Privacy</h3>
              <p className="text-muted-foreground">
                PayMig is designed for family use. Children's accounts (MobPay/WatchPay)
                are managed by and linked to parent accounts. We collect minimal data from
                child accounts and parents have full control over their children's data.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold">9. Cookies and Tracking</h3>
              <p className="text-muted-foreground">
                We use essential cookies for authentication and session management.
                We do not use third-party tracking cookies or sell data to advertisers.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold">10. Changes to This Policy</h3>
              <p className="text-muted-foreground">
                We may update this Privacy Policy from time to time. We will notify you
                of material changes through the app or via email. The "Last updated" date
                at the top indicates when the policy was last revised.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold">11. Contact Us</h3>
              <p className="text-muted-foreground">
                If you have questions about this Privacy Policy or wish to exercise your
                data rights, please contact our Data Protection Officer at:
              </p>
              <p className="text-muted-foreground mt-2">
                Email: privacy@paymig.com<br />
                Address: PayMig AB, Blekinge, Sweden
              </p>
            </section>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
